import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { scopedActionsCreators, scopedStateSelectors } from '@makemydeal/shared-scoped-store';

/**
 * Clears the focused scope when the user clicks outside scenario containers and slideouts.
 * This causes hotkeys to fall back to the primary scenario.
 *
 * A click is considered "outside" if the target is NOT inside:
 * - A scenario card (identified by `[data-scope-container]`)
 * - A slideout/dialog (identified by `[role="dialog"]`)
 * - A floating menu (identified by `[role="menu"]`) — rendered via FloatingPortal at body level
 * - A dropdown listbox (identified by `[role="listbox"]`) — rendered via Portal for select inputs
 * - A presentation overlay (identified by `[role="presentation"]`) — used by MUI Popover/Modal
 * - A tooltip/popper (identified by `[role="tooltip"]`) — used by MUI Popper for ComboBox dropdowns
 * - A SplitButton meatball/toggle button — these open dropdown menus via internal state
 *   and a re-render caused by dispatch would prevent the menu from opening
 */
export const useClearFocusOnOutsideClick = (enabled = true): void => {
    const dispatch = useDispatch();
    const focusedScopeId = useSelector(scopedStateSelectors.getFocusedScopeId);
    const focusedScopeIdRef = useRef(focusedScopeId);
    useEffect(() => {
        focusedScopeIdRef.current = focusedScopeId;
    }, [focusedScopeId]);

    const handleMouseDown = useCallback(
        (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target?.closest) return;

            if (focusedScopeIdRef.current === undefined) return;

            const isInsideScopeContainer = target.closest('[data-scope-container]');
            const isInsideDialog = target.closest('[role="dialog"]');
            const isInsideFloatingElement = target.closest(
                '[role="menu"], [role="listbox"], [role="presentation"], [role="tooltip"]'
            );
            const isInsideSplitButtonToggle = target.closest('[data-testid*="meatBallButton"]');

            if (!isInsideScopeContainer && !isInsideDialog && !isInsideFloatingElement && !isInsideSplitButtonToggle) {
                dispatch(scopedActionsCreators.setFocusedScopeId(undefined));
            }
        },
        [dispatch]
    );

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        document.addEventListener('mousedown', handleMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [handleMouseDown, enabled]);
};
